import type { Dispatch } from 'redux';
import {
    FavoriteEntity,
    FavoritePerson,
    FavoritePrivateDistributionList,
    createDataActionFactory,
    Feature,
    PersonType,
    makeHubPersonaId,
    makeHubGroupPersonaId,
    FavoriteUnifiedGroup,
    DistributionListMember,
} from '../../../shared';
import { isGroupsEnabled } from 'owa-account-capabilities/lib/isGroupsEnabled';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import getFavorites from 'owa-session-store/lib/utils/getFavorites';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    FolderForestNodeType,
    FavoritePersonaData,
    FavoritePrivateDistributionListData,
    OutlookFavoriteServiceDataType,
    FavoritePersonaNode,
    FavoriteGroupData,
} from 'owa-favorites-types';
import type { PrivateDistributionListMember } from 'owa-persona-models';

const getOwaMailFavoritesStoreModule = () =>
    import(/* webpackChunkName: "OwaMailFavoritesStore" */ 'owa-mail-favorites-store');

const getOwaFavoritesModule = () => import(/* webpackChunkName: "OwaFavorites" */ 'owa-favorites');

export const loadFavoritesCompletedAction = createDataActionFactory<
    { favorites: FavoriteEntity[] },
    Error
>('LoadOutlookFavoritesCompletedAction', Feature.Favorite, action => {
    const favoritesLoaded = action.payload.favorites || [];
    return {
        count: favoritesLoaded.length,
        favoriteIdsLoaded: favoritesLoaded.map(favorite => favorite.favoriteId).join(','),
    };
});

export function loadFavorites(dispatch: Dispatch): () => Promise<void> {
    if (isFeatureEnabled('tri-favorites-roaming')) {
        return loadOutlookFavorites(dispatch);
    } else {
        return loadFavoritesFromUserOptions(dispatch);
    }
}

export const loadFavoritesFromUserOptions = (dispatch: Dispatch) => () =>
    // Init owa-stores
    getOwaFavoritesModule().then(module => {
        module.loadFavorites();

        // Init favoriteRootFolder if necessary
        onFavoritesLoaded();

        // Get config and parse nodes
        const userOptions = getUserConfiguration().UserOptions;
        const convertedPersonas: FavoritePerson[] = [];

        if (userOptions.FavoriteNodes) {
            userOptions.FavoriteNodes.forEach(rawNode => {
                const parsedNode = module.parseFavoriteNode(rawNode) as FavoritePersonaNode;
                if (parsedNode && parsedNode.type === FolderForestNodeType.Persona) {
                    convertedPersonas.push({
                        type: PersonType.FavoritePerson,
                        hubPersonaId: parsedNode.id,
                        personaId: parsedNode.personaId,
                        favoriteId: parsedNode.id,
                        info: {
                            displayName: parsedNode.displayName,
                            allEmailAddresses: parsedNode.allEmailAddresses,
                            mainEmailAddress: parsedNode.mainEmailAddress,
                        },
                    });
                }
            });
        }
        dispatch(loadFavoritesCompletedAction.success({ favorites: convertedPersonas }));
    });

export const loadOutlookFavorites = (dispatch: Dispatch) => () =>
    // Load Favorites module
    getOwaFavoritesModule().then(module => {
        // Get Favorites from UserConfig
        const favoritesObj = getFavorites();
        const outlookFavorites = favoritesObj ? favoritesObj.value || [] : [];

        // Filter personas and process
        const convertedPersonas: FavoriteEntity[] = outlookFavorites
            .map(favoriteItem => {
                const type = favoriteItem.Type.toLocaleLowerCase();

                if (type === 'persona') {
                    // Convert to FavoritePersonaData
                    const favoriteData = module.convertServiceResponseToFavoriteData(
                        favoriteItem as OutlookFavoriteServiceDataType,
                        'persona'
                    ) as FavoritePersonaData;

                    // Convert to person
                    const favorite: FavoritePerson = {
                        type: PersonType.FavoritePerson,
                        hubPersonaId: makeHubPersonaId(favoriteData.favoriteId),
                        favoriteId: favoriteData.favoriteId,
                        // Convert Rest Id to EWS Id used by PH
                        personaId: favoriteData.personaId
                            ? module.convertRestFolderIdToEWSFolderId(favoriteData.personaId)
                            : undefined,
                        info: {
                            displayName: favoriteData.displayName,
                            allEmailAddresses: [...favoriteData.allEmailAddresses], //Convert from ObservableArray
                            mainEmailAddress: favoriteData.mainEmailAddress,
                        },
                    };

                    return favorite;
                }

                if (type === 'privatedistributionlist' && isFeatureEnabled('peo-favoritePdls')) {
                    // Convert to FavoritePersonaData
                    const favoriteData = module.convertServiceResponseToFavoriteData(
                        favoriteItem as OutlookFavoriteServiceDataType,
                        'privatedistributionlist'
                    ) as FavoritePrivateDistributionListData;

                    // Convert to person
                    const favorite: FavoritePrivateDistributionList = {
                        type: PersonType.FavoritePrivateDistributionList,
                        hubPersonaId: makeHubPersonaId(favoriteData.favoriteId),
                        favoriteId: favoriteData.favoriteId,
                        info: {
                            itemId: module.convertRestFolderIdToEWSFolderId(favoriteData.pdlId),
                            displayName: favoriteData.displayName,
                            members: convertFavoriteMembersToPdlMembers(favoriteData.members),
                        },
                    };

                    return favorite;
                }

                if (type === 'group' && isGroupsEnabled()) {
                    // Convert to FavoritePersonaData
                    const favoriteData = module.convertServiceResponseToFavoriteData(
                        favoriteItem as OutlookFavoriteServiceDataType,
                        'group'
                    ) as FavoriteGroupData;

                    // Convert to group
                    const favorite: FavoriteUnifiedGroup = {
                        type: PersonType.FavoriteUnifiedGroup,
                        hubPersonaId: makeHubGroupPersonaId(
                            favoriteData.groupExternalDirectoryObjectId,
                            favoriteData.groupSmtp
                        ),
                        favoriteId: favoriteData.favoriteId,
                        info: {
                            displayName: favoriteData.displayName,
                            emailAddress: favoriteData.groupSmtp,
                            externalDirectoryObjectId: favoriteData.groupExternalDirectoryObjectId,
                        },
                    };

                    return favorite;
                }

                return undefined;
            })
            .filter(favorite => !!favorite);

        dispatch(loadFavoritesCompletedAction.success({ favorites: convertedPersonas }));
    });

function convertFavoriteMembersToPdlMembers(
    pdlMembers: PrivateDistributionListMember[]
): DistributionListMember[] {
    return pdlMembers.map(member => ({
        type: PersonType.PrivateDistributionListMember,
        displayName: member.name,
        emailAddressWrapper: { Name: member.emailAddress, EmailAddress: member.emailAddress },
        isPdl: false,
        isManuallyTypedEmail: false,
    }));
}

// This function call will be removed once logic from initializeFavoritePersonas is extracted in owa-favorites
function onFavoritesLoaded(): void {
    getOwaFavoritesModule().then(module => {
        const favoritesStore = module.getStore();
        const favoritePersonaNodes = favoritesStore.favoritesPersonaNodes;
        if (favoritePersonaNodes.size > 0) {
            getOwaMailFavoritesStoreModule().then(module =>
                module.lazyInitializeFavoritePersonas.importAndExecute(
                    [...favoritePersonaNodes.values()].map(persona => persona.id)
                )
            );
        }
    });
}
export type { Action } from '../../../shared';
export type { DataActionFactory } from '../../../shared';
